import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import API from '../../api';
import {
    archiveUser,
    activateUser,
    resendUserInvite,
    permanentlyRemoveUser,
    checkStudentArchive,
    archiveStudentGuided,
} from '../../api/users';
import { showConfirm } from '../../utils/popup';
import { getApiErrorInfo } from '../../utils/apiError';
import ArchiveStudentModal from '../students/ArchiveStudentModal';
import { studentDisplayName } from '../../utils/displayNames';

/**
 * Shared row actions for admin / manager user lists (students, coaches, managers).
 *
 * variant="manager" — email sign-in link via manager API; shows "Remove from list" instead of archive/activate.
 */
export default function UserRowActions({
    user,
    entityLabel,
    editPath,
    onStatusChange,
    licenseAtLimit = false,
    extraActions = null,
    variant = 'admin',
    onRemoveFromList = null,
}) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const userId = user?.id ?? user?.userId ?? user?.user_id;
    const isActive = user?.active !== false;
    const hasEmail = Boolean((user?.email || '').trim());
    const label = entityLabel || 'user';
    const translatedLabel = t(`userActions.labels.${label}`, { defaultValue: label });
    const isStudent = label === 'student';
    const studentName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim()
        || user?.displayName
        || user?.username
        || studentDisplayName(null, userId, t);
    const countsTowardLicense = label === 'student' || label === 'coach';
    const showPermanentRemove = variant === 'admin' && licenseAtLimit && !isActive && countsTowardLicense;
    const isManager = variant === 'manager';

    const [archiveModalOpen, setArchiveModalOpen] = useState(false);
    const [archiveCheck, setArchiveCheck] = useState(null);
    const [archiveCheckLoading, setArchiveCheckLoading] = useState(false);
    const [archiveSubmitting, setArchiveSubmitting] = useState(false);

    const handleResendInvite = async () => {
        if (!hasEmail) {
            alert(t('accountAccess.signInLink.noEmailAlert', { role: translatedLabel }));
            return;
        }
        if (!await showConfirm(t('accountAccess.signInLink.listConfirm', { role: translatedLabel }))) {
            return;
        }
        try {
            if (isManager) {
                await API.post(`/manager/users/${userId}/invite`);
            } else {
                await resendUserInvite(userId);
            }
            alert(t('accountAccess.signInLink.success'));
        } catch (err) {
            console.error(err);
            alert(getApiErrorInfo(err, t('accountAccess.signInLink.failure')).message);
        }
    };

    const openStudentArchiveModal = async () => {
        setArchiveModalOpen(true);
        setArchiveCheckLoading(true);
        setArchiveCheck(null);
        try {
            const res = await checkStudentArchive(userId);
            setArchiveCheck(res?.data ?? res);
        } catch (err) {
            console.error(err);
            setArchiveModalOpen(false);
            alert(getApiErrorInfo(err, t('userActions.loadArchiveFailed', { label: translatedLabel })).message);
        } finally {
            setArchiveCheckLoading(false);
        }
    };

    const handleArchive = async () => {
        if (isStudent) {
            await openStudentArchiveModal();
            return;
        }
        if (!await showConfirm(t('userActions.archiveConfirm', { label: translatedLabel }))) {
            return;
        }
        try {
            await archiveUser(userId);
            onStatusChange?.();
        } catch (err) {
            console.error(err);
            alert(getApiErrorInfo(err, t('userActions.archiveFailed', { label: translatedLabel })).message);
        }
    };

    const handleStudentArchiveConfirm = async (payload) => {
        setArchiveSubmitting(true);
        try {
            if (archiveCheck?.hasActiveEnrollments) {
                await archiveStudentGuided(userId, payload);
            } else {
                await archiveUser(userId);
            }
            setArchiveModalOpen(false);
            onStatusChange?.();
        } catch (err) {
            console.error(err);
            alert(getApiErrorInfo(err, t('userActions.archiveFailed', { label: translatedLabel })).message);
        } finally {
            setArchiveSubmitting(false);
        }
    };

    const handlePermanentRemove = async () => {
        if (!await showConfirm(t('userActions.permanentRemoveConfirm', { label: translatedLabel }))) {
            return;
        }
        try {
            await permanentlyRemoveUser(userId);
            onStatusChange?.();
        } catch (err) {
            console.error(err);
            alert(getApiErrorInfo(err, t('userActions.removeFailed', { label: translatedLabel })).message);
        }
    };

    const handleActivate = async () => {
        if (!await showConfirm(t('userActions.reactivateConfirm', { label: translatedLabel }))) {
            return;
        }
        try {
            await activateUser(userId);
            onStatusChange?.();
        } catch (err) {
            console.error(err);
            alert(getApiErrorInfo(err, t('userActions.activateFailed', { label: translatedLabel })).message);
        }
    };

    const handleRemoveFromList = async () => {
        if (onRemoveFromList) {
            await onRemoveFromList(user);
        }
    };

    return (
        <>
            <div className="flex flex-wrap items-center gap-2">
                <button
                    type="button"
                    onClick={() => navigate(editPath)}
                    className="btn-action-edit"
                    title={t('userActions.editTitle', { label: translatedLabel })}
                >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                    </svg>
                    {t('userActions.edit')}
                </button>

                <button
                    type="button"
                    onClick={handleResendInvite}
                    className="btn-action-invite disabled:cursor-not-allowed disabled:opacity-50"
                    title={hasEmail ? t('accountAccess.signInLink.buttonTitle') : t('accountAccess.signInLink.buttonTitleNoEmail')}
                    disabled={!hasEmail}
                >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                    </svg>
                    {t('accountAccess.signInLink.button')}
                </button>

                {isManager ? (
                    isActive && onRemoveFromList && (
                        <button
                            type="button"
                            onClick={handleRemoveFromList}
                            className="btn-action-archive"
                            title={t('userActions.removeFromListTitle')}
                        >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                            </svg>
                            {t('userActions.removeFromList')}
                        </button>
                    )
                ) : isActive ? (
                    <button
                        type="button"
                        onClick={handleArchive}
                        className="btn-action-archive"
                        title={t('userActions.archiveTitle', { label: translatedLabel })}
                    >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
                            />
                        </svg>
                        {t('userActions.archive')}
                    </button>
                ) : (
                    <>
                        {showPermanentRemove && (
                            <button
                                type="button"
                                onClick={handlePermanentRemove}
                                className="btn-action-permanent-remove"
                                title={t('userActions.removePermanentlyTitle')}
                            >
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                    />
                                </svg>
                                {t('userActions.removePermanently')}
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={handleActivate}
                            className="btn-action-activate"
                            title={t('userActions.reactivateTitle', { label: translatedLabel })}
                        >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                            </svg>
                            {t('userActions.activate')}
                        </button>
                    </>
                )}

                {extraActions}
            </div>

            {isStudent && (
                <ArchiveStudentModal
                    open={archiveModalOpen}
                    userId={userId}
                    studentName={studentName}
                    archiveCheck={archiveCheck}
                    loadingCheck={archiveCheckLoading}
                    submitting={archiveSubmitting}
                    onConfirm={handleStudentArchiveConfirm}
                    onCancel={() => {
                        if (!archiveSubmitting) setArchiveModalOpen(false);
                    }}
                />
            )}
        </>
    );
}
